/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    static isB2BUnlocked() {
      if (window.mTacIsB2B && window.mTacIsB2B()) return true;
      if (document.documentElement.classList.contains('bss-b2b-unlocked')) return true;
      const tags = window.BSS_B2B?.shopData?.customer?.tags;
      if (typeof tags === 'string') {
        return tags.split(',').map((t) => t.trim()).includes('B2B');
      }
      if (Array.isArray(tags)) {
        return tags.map((t) => String(t).trim()).includes('B2B');
      }
      return false;
    }

    static refreshBssB2bPrice(variantId, rootEl, variantObj, selectedOptions, triggerEl) {
      if (!VariantPicker.isB2BUnlocked()) {
        window.mTacB2bLog && window.mTacB2bLog('[B2B] refresh skipped: not unlocked', { variantId });
        return;
      }
      const root = rootEl || document;
      const priceWrap = root.querySelector('.product-info__price');
      const b2bPrice = priceWrap ? priceWrap.querySelector('.price-b2b') : root.querySelector('.price-b2b');
      if (!priceWrap && !b2bPrice) {
        window.mTacB2bLog && window.mTacB2bLog('[B2B] refresh skipped: missing price container', { variantId });
        return;
      }

      if (variantId) {
        if (priceWrap) {
          priceWrap.setAttribute('bss-b2b-variant-id', String(variantId));
        }
        if (b2bPrice) {
          b2bPrice.setAttribute('bss-b2b-variant-id', String(variantId));
        }
      }

      if (priceWrap && b2bPrice && !priceWrap.hasAttribute('bss-b2b-product-id')) {
        const productId = b2bPrice.getAttribute('bss-b2b-product-id');
        if (productId) {
          priceWrap.setAttribute('bss-b2b-product-id', productId);
        }
      }

      const resolvePicker = () => {
        if (root && typeof root.querySelector === 'function') {
          return root.querySelector('variant-picker');
        }
        return null;
      };

      const picker = resolvePicker();
      const resolvedVariant = variantObj || picker?.variant || null;
      const resolvedOptions = selectedOptions || (picker?.getSelectedOptions ? picker.getSelectedOptions() : null);
      const resolveTriggerEl = (el) => {
        if (!el || el.nodeType !== 1) return null;
        if (el.matches && el.matches('input, select, option')) return el;
        if (el.closest) {
          const closest = el.closest('input, select, option');
          if (closest) return closest;
        }
        return el;
      };
      const safeTriggerEl = resolveTriggerEl(triggerEl);

      const kick = () => {
        window.mTacB2bLog && window.mTacB2bLog('[B2B] refresh kick', {
          variantId,
          hasPriceWrap: !!priceWrap,
          hasB2bPrice: !!b2bPrice
        });

        if (window.BSS_B2B) {
          try {
            if (resolvedVariant && typeof window.BSS_B2B.onChangeVariant === 'function') {
              window.BSS_B2B.onChangeVariant(resolvedVariant);
            }
          } catch (e) {
            window.mTacB2bLog && window.mTacB2bLog('[B2B] onChangeVariant failed', e);
          }

          try {
            if (typeof window.BSS_B2B.changeProductPriceWithInterval === 'function') {
              window.BSS_B2B.changeProductPriceWithInterval();
            }
          } catch (e) {
            window.mTacB2bLog && window.mTacB2bLog('[B2B] changeProductPriceWithInterval failed', e);
          }

          try {
            if (safeTriggerEl && typeof window.BSS_B2B.changeVariantOptionProductPage === 'function') {
              window.mTacB2bLog && window.mTacB2bLog('[B2B] changeVariantOptionProductPage call', {
                variantId,
                triggerTag: safeTriggerEl.tagName,
                triggerName: safeTriggerEl.getAttribute && safeTriggerEl.getAttribute('name')
              });
              window.BSS_B2B.changeVariantOptionProductPage(safeTriggerEl, variantId);
            }
          } catch (e) {
            window.mTacB2bLog && window.mTacB2bLog('[B2B] changeVariantOptionProductPage failed', e);
          }
        }

        document.dispatchEvent(new CustomEvent('bss:b2b:variant-change', {
          detail: { variantId }
        }));
        document.dispatchEvent(new CustomEvent('bss:b2b:price:refresh', {
          detail: { variantId }
        }));

        // Fallback: if app doesn't update, try to apply price from BSS data stores.
        window.setTimeout(() => {
          VariantPicker.applyB2BPriceFromBssData(variantId, root);
        }, 80);

        // Force re-scan of the price node in case the app observes DOM mutations.
        const nodeToReattach = priceWrap || b2bPrice;
        if (nodeToReattach) {
          try {
            const parent = nodeToReattach.parentNode;
            const next = nodeToReattach.nextSibling;
            nodeToReattach.remove();
            if (next) {
              parent.insertBefore(nodeToReattach, next);
            } else {
              parent.appendChild(nodeToReattach);
            }
          } catch (e) {
            // No-op: app should still pick up via events/attributes.
          }
        }
      };

      kick();

      // Retry to win against late theme DOM updates.
      const retryKey = `bssRetry_${variantId || 'none'}`;
      if (priceWrap && priceWrap.dataset[retryKey]) return;
      if (!priceWrap) return;
      priceWrap.dataset[retryKey] = '1';
      window.setTimeout(kick, 50);
      window.setTimeout(kick, 300);
    }

    static applyB2BPriceFromBssData(variantId, rootEl) {
      if (!VariantPicker.isB2BUnlocked()) return false;
      if (!variantId) return false;
      const root = rootEl || document;
      const b2bPrice = root.querySelector('.price-b2b');
      if (!b2bPrice) return false;

      const entry = VariantPicker.findBssEntryForVariant(variantId);
      if (!entry) {
        window.mTacB2bLog && window.mTacB2bLog('[B2B] fallback: no entry for variant', { variantId });
        return false;
      }

      const priceData = VariantPicker.extractPriceFromEntry(entry);
      if (!priceData || priceData.price == null) {
        window.mTacB2bLog && window.mTacB2bLog('[B2B] fallback: no price in entry', { variantId, entry });
        return false;
      }

      const formattedPrice = VariantPicker.formatMoney(priceData.price);
      const formattedCompare = priceData.compareAt ? VariantPicker.formatMoney(priceData.compareAt) : null;

      const currentEl = b2bPrice.querySelector('.price__current');
      if (currentEl) {
        currentEl.textContent = formattedPrice;
      } else {
        b2bPrice.textContent = formattedPrice;
      }

      // Ensure compare-at element exists if applicable.
      if (formattedCompare) {
        let compareEl = root.querySelector('.bss-b2b-customized-compare-at-price');
        if (!compareEl) {
          compareEl = document.createElement('span');
          compareEl.className = 'bss-b2b-customized-compare-at-price';
          compareEl.style.textDecoration = 'line-through';
          compareEl.style.margin = '0 1rem 0 0';
          compareEl.style.color = '#303030';
          b2bPrice.parentNode.insertBefore(compareEl, b2bPrice);
        }
        compareEl.textContent = formattedCompare;
      }

      window.mTacB2bLog && window.mTacB2bLog('[B2B] fallback applied', {
        variantId,
        price: priceData.price,
        compareAt: priceData.compareAt
      });
      return true;
    }

    /**
     * Directly updates .price-b2b from the wh-product-data JSON embedded in the page.
     * This is a reliable, zero-latency fallback that doesn't depend on BSS B2B data stores.
     */
    static applyB2BPriceFromProductJson(variantId, rootEl) {
      if (!VariantPicker.isB2BUnlocked()) return false;
      if (!variantId) return false;
      const root = rootEl || document;

      const b2bEl = root.querySelector('.price-b2b');
      if (!b2bEl) return false;

      const productId = b2bEl.getAttribute('bss-b2b-product-id');
      if (!productId) return false;

      const dataEl = document.getElementById('wh-product-data-' + productId);
      if (!dataEl) return false;

      let productData;
      try { productData = JSON.parse(dataEl.textContent); } catch (e) { return false; }

      const variantData = productData && productData.variants && productData.variants[String(variantId)];
      if (!variantData) return false;

      // Determine which price key to use based on customer tags
      let priceKey = 'wholesalePrice';
      try {
        const storeDataEl = document.getElementById('bss-b2b-store-data');
        if (storeDataEl) {
          const storeData = JSON.parse(storeDataEl.textContent);
          const tags = (storeData.customer && storeData.customer.tags) || [];
          const tagStr = (Array.isArray(tags) ? tags.join(',') : String(tags)).toLowerCase();
          if (tagStr.includes('rfq')) priceKey = 'rfqPrice';
        }
      } catch (e) { /* use default wholesalePrice */ }

      const b2bCents = variantData[priceKey] || 0;
      if (b2bCents <= 0) return false;

      const fmt = (cents) => (window.Shopify && typeof window.Shopify.formatMoney === 'function'
        ? window.Shopify.formatMoney(cents)
        : '$' + (cents / 100).toFixed(2));

      const currentEl = b2bEl.querySelector('.price__current');
      if (currentEl) currentEl.textContent = fmt(b2bCents);

      const regularCents = variantData.price || 0;
      let wasEl = b2bEl.querySelector('.price__was');
      if (regularCents > b2bCents) {
        if (!wasEl) {
          wasEl = document.createElement('span');
          wasEl.className = 'price__was';
          b2bEl.appendChild(wasEl);
        }
        wasEl.textContent = fmt(regularCents);
        wasEl.hidden = false;
      } else if (wasEl) {
        wasEl.textContent = '';
        wasEl.hidden = true;
      }

      window.mTacB2bLog && window.mTacB2bLog('[B2B] product JSON price applied', {
        variantId, priceKey, b2bCents, regularCents
      });
      return true;
    }

    static findBssEntryForVariant(variantId) {
      const bss = window.BSS_B2B;
      if (!bss) return null;
      const idStr = String(variantId);
      const idNum = Number(variantId);

      const tryContainer = (container) => {
        if (!container) return null;
        if (container instanceof Map) {
          return container.get(idNum) || container.get(idStr) || null;
        }
        if (Array.isArray(container)) {
          return container.find((item) => {
            const vid = item?.variant_id ?? item?.variantId ?? item?.id;
            return vid != null && String(vid) === idStr;
          }) || null;
        }
        if (typeof container === 'object') {
          if (container[idStr]) return container[idStr];
          if (container[idNum]) return container[idNum];
        }
        return null;
      };

      const sources = [
        bss.cart && bss.cart.variantStorage,
        bss.cart && bss.cart.itemsMap,
        bss.cart && bss.cart.productMap,
        bss.cart && bss.cart.updatedLineItems,
        bss.cp && bss.cp.productMapAfterUseCP,
        bss.cp && bss.cp.productAfterUseCP
      ];

      for (let i = 0; i < sources.length; i += 1) {
        const hit = tryContainer(sources[i]);
        if (hit) return hit;
      }

      return null;
    }

    static extractPriceFromEntry(entry) {
      if (!entry || typeof entry !== 'object') return null;
      const priceKeys = [
        'price_apply',
        'price_applied',
        'price',
        'final_price',
        'b2b_price',
        'modified_price',
        'discount_price',
        'price_after',
        'price_apply_cp'
      ];
      const compareKeys = [
        'compare_at_price',
        'origin_price',
        'original_price',
        'price_origin',
        'price_before',
        'compare_at'
      ];

      const pick = (keys) => {
        for (let i = 0; i < keys.length; i += 1) {
          const val = entry[keys[i]];
          const num = VariantPicker.normalizeMoneyValue(val);
          if (num != null) return num;
        }
        return null;
      };

      const price = pick(priceKeys);
      const compareAt = pick(compareKeys);
      return { price, compareAt };
    }

    static normalizeMoneyValue(val) {
      if (val == null) return null;
      if (typeof val === 'number' && !Number.isNaN(val)) return val;
      if (typeof val === 'string') {
        const cleaned = val.replace(/[^0-9.]/g, '');
        if (!cleaned) return null;
        const num = Number(cleaned);
        if (!Number.isNaN(num)) return num;
      }
      return null;
    }

    static formatMoney(val) {
      const bss = window.BSS_B2B;
      const num = VariantPicker.normalizeMoneyValue(val);
      if (num == null) return '';
      if (bss && typeof bss.formatMoney === 'function') {
        // BSS formatMoney expects cents; detect if value looks like dollars.
        const cents = num < 1000 && num % 1 !== 0 ? Math.round(num * 100) : num;
        return bss.formatMoney(cents);
      }
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        const cents = num < 1000 && num % 1 !== 0 ? Math.round(num * 100) : num;
        return window.Shopify.formatMoney(cents);
      }
      return `$${(num / 100).toFixed(2)}`;
    }
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.nativeSelector = document.getElementById(`variants-${this.section.dataset.section}`);
      this.data = this.getVariantData();
      this.optionValueData = this.getOptionValueData();
      this.useGranularAvailability = !!(this.optionValueData && this.data && (
        this.dataset.variantsLimited === 'true' || (this.data.variants && this.data.variants.length >= 250)
      ));
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));
      this.applySearchParams();

      this.setAttribute('loaded', '');
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      if (this.useGranularAvailability) {
        this.updatePickupAvailability();
        VariantPicker.updateLabelText(evt);
        this.refreshOptionValueData();
        this.refreshVariantDetails(evt);
      } else {
        this.updatePrice(evt);
        this.updateBackorderText();
        this.updatePickupAvailability();
        this.updateSku();
        this.updateBarcode();
        VariantPicker.updateLabelText(evt);
      }

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          allVariants: this.data.variants,
          selectedOptions: this.selectedOptions
        }
      }));
    }

    /**
     * Updates the "Add to Cart" button label and status.
     */
    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const hasVariant = this.useGranularAvailability
        ? !!this.getSelectedVariantIdFromOptionValues()
        : !!this.variant;
      const variantAvailable = this.useGranularAvailability
        ? this.getSelectedVariantAvailabilityFromOptionValues()
        : this.variant && this.variant.available;
      const unavailableStr = hasVariant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        const labelEl = el.nextElementSibling;
        if (labelEl) labelEl.title = unavailableText;
      } else {
        const labelEl = el.nextElementSibling;
        if (labelEl) labelEl.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.availability === 'off') return;
      if (this.useGranularAvailability) {
        this.updateAvailabilityFromOptionValues();
        return;
      }
      const { availabilityMode } = this.dataset; // 'down' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMode === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates availability using product_option_value data.
     */
    updateAvailabilityFromOptionValues() {
      const optionsData = this.optionValueData && this.optionValueData.options;
      if (!optionsData) return;

      this.optionSelectors.forEach((selector) => {
        const optionData = optionsData.find((opt) => opt.name === selector.dataset.option);
        if (!optionData) return;

        const options = selector.querySelectorAll('.js-option:not([data-value=""])');
        options.forEach((optionEl) => {
          const optionValue = selector.dataset.selectorType === 'dropdown' ? optionEl.dataset.value : optionEl.value;
          const valueData = optionData.values.find((val) => val.value === optionValue);
          const exists = !!(valueData && valueData.variant_id);
          const available = !!(valueData && valueData.available);
          VariantPicker.updateOptionAvailability(optionEl, exists, available);
        });
      });
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const formatted = this.data.formatted && this.data.formatted[this.variant.id];
        if (!formatted) {
          this.backorder.hidden = true;
          return;
        }
        const { inventory } = formatted;

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the colour option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      const variantId = this.useGranularAvailability
        ? this.getSelectedVariantIdFromOptionValues()
        : this.variant && this.variant.id;
      const variantAvailable = this.useGranularAvailability
        ? this.getSelectedVariantAvailabilityFromOptionValues()
        : this.variant && this.variant.available;

      if (variantId && variantAvailable) {
        this.pickUpAvailability.getAvailability(variantId);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice(evt) {
      this.price = this.price || this.section.querySelector('.product-info__price .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price) variant = this.data.variants[i];
        }
      }

      if (variant) {
        const formatted = this.data.formatted && this.data.formatted[variant.id];
        if (!formatted) {
          this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
          this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
          const from = this.price.querySelector('.price__from');
          if (from) {
            from.hidden = !this.preSelection;
          }
          return;
        }
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = formatted.price;
        if (priceWasEl) priceWasEl.innerHTML = formatted.compareAtPrice || '';

        // Update unit price, if specified.
        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = formatted.unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        this.price.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        this.price.classList.toggle('price--sold-out', !variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }

      VariantPicker.refreshBssB2bPrice(
        this.variant && this.variant.id,
        this.section,
        this.variant,
        this.selectedOptions,
        evt && evt.target
      );

      // Directly update .price-b2b from embedded product JSON — reliable, no BSS B2B dependency.
      VariantPicker.applyB2BPriceFromProductJson(
        this.variant && this.variant.id,
        this.section
      );
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcode = this.barcode || this.section.querySelector('.product-barcode__value');
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      const variantId = this.useGranularAvailability
        ? this.getSelectedVariantIdFromOptionValues()
        : this.variant && this.variant.id;
      const url = variantId ? `${this.dataset.url}?variant=${variantId}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        const variantId = this.useGranularAvailability
          ? this.getSelectedVariantIdFromOptionValues()
          : this.variant && this.variant.id;
        input.value = variantId || '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]:not(.js-variant-options-data)');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Gets the product_option_value data for a product.
     * @returns {?object}
     */
    getOptionValueData() {
      const dataEl = this.querySelector('.js-variant-options-data');
      return dataEl ? JSON.parse(dataEl.textContent) : null;
    }

    /**
     * Gets the selected variant id from product_option_value data.
     * @returns {?string}
     */
    getSelectedVariantIdFromOptionValues({ preference } = {}) {
      const optionsData = this.optionValueData && this.optionValueData.options;
      if (!optionsData) return null;

      const order = preference === 'first'
        ? [...Array(this.selectedOptions.length).keys()]
        : [...Array(this.selectedOptions.length).keys()].reverse();

      for (let i = 0; i < order.length; i += 1) {
        const index = order[i];
        const selectedValue = this.selectedOptions[index];
        if (!selectedValue) continue;
        const selector = this.optionSelectors[index];
        const optionData = selector
          ? optionsData.find((opt) => opt.name === selector.dataset.option)
          : optionsData[index];
        if (!optionData) continue;
        const valueData = optionData.values.find((val) => val.value === selectedValue);
        if (valueData && valueData.variant_id) return String(valueData.variant_id);
      }

      return null;
    }

    /**
     * Gets availability for the selected variant from product_option_value data.
     * @returns {boolean}
     */
    getSelectedVariantAvailabilityFromOptionValues() {
      const optionsData = this.optionValueData && this.optionValueData.options;
      if (!optionsData) return false;

      for (let i = this.selectedOptions.length - 1; i >= 0; i -= 1) {
        const selectedValue = this.selectedOptions[i];
        if (!selectedValue) continue;
        const selector = this.optionSelectors[i];
        const optionData = selector
          ? optionsData.find((opt) => opt.name === selector.dataset.option)
          : optionsData[i];
        if (!optionData) continue;
        const valueData = optionData.values.find((val) => val.value === selectedValue);
        if (valueData && valueData.variant_id) return !!valueData.available;
      }

      return false;
    }

    /**
     * Gets media id for the selected variant from product_option_value data.
     * @returns {?number}
     */
    getSelectedVariantMediaIdFromOptionValues() {
      const optionsData = this.optionValueData && this.optionValueData.options;
      if (!optionsData) return null;

      for (let i = this.selectedOptions.length - 1; i >= 0; i -= 1) {
        const selectedValue = this.selectedOptions[i];
        if (!selectedValue) continue;
        const selector = this.optionSelectors[i];
        const optionData = selector
          ? optionsData.find((opt) => opt.name === selector.dataset.option)
          : optionsData[i];
        if (!optionData) continue;
        const valueData = optionData.values.find((val) => val.value === selectedValue);
        if (valueData && valueData.media_id) return Number(valueData.media_id);
      }

      return null;
    }

    /**
     * Refresh price/sku/barcode/backorder using Section Rendering API.
     */
    refreshVariantDetails(evt) {
      const variantId = this.getSelectedVariantIdFromOptionValues();
      if (!variantId || !this.dataset.sectionId || !this.dataset.url) return;

      if (this.variantDetailsAbortController) {
        this.variantDetailsAbortController.abort();
      }

      this.variantDetailsAbortController = new AbortController();
      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', this.dataset.sectionId);

      fetch(url.toString(), { signal: this.variantDetailsAbortController.signal })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.text();
        })
        .then((response) => {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = response;
          const sectionRoot = tmpl.content.querySelector(`#shopify-section-${this.dataset.sectionId}`) || tmpl.content;
          const root = this.section;

          const replaceEl = (selector) => {
            const currentEl = root.querySelector(selector);
            const newEl = sectionRoot.querySelector(selector);
            if (!currentEl || !newEl) return;
            if (selector === '.product-info__price') {
              // Keep the original wrapper to preserve app bindings (e.g. BSS B2B),
              // but swap its contents to the newly rendered markup.
              const existingB2b = currentEl.querySelector('.price-b2b');
              currentEl.innerHTML = newEl.innerHTML;

              if (existingB2b) {
                const incomingB2b = currentEl.querySelector('.price-b2b');
                if (incomingB2b) incomingB2b.remove();
                currentEl.appendChild(existingB2b);
              }

              VariantPicker.refreshBssB2bPrice(
                variantId,
                root,
                this.variant,
                this.selectedOptions,
                evt && evt.target
              );
              return;
            }

            currentEl.replaceWith(newEl);
          };

          replaceEl('.product-info__price');
          replaceEl('.product-sku');
          replaceEl('.product-barcode');
          replaceEl('.backorder');
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          console.warn(error);
        });
    }

    /**
     * Refresh option value data using Section Rendering API.
     */
    refreshOptionValueData() {
      const variantId = this.getSelectedVariantIdFromOptionValues({ preference: 'first' });
      if (!variantId || !this.dataset.sectionId || !this.dataset.url) return;

      if (this.optionValueAbortController) {
        this.optionValueAbortController.abort();
      }

      this.optionValueAbortController = new AbortController();
      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', this.dataset.sectionId);

      fetch(url.toString(), { signal: this.optionValueAbortController.signal })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.text();
        })
        .then((response) => {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = response;
          const dataEl = tmpl.content.querySelector('.js-variant-options-data');
          if (!dataEl) return;
          this.optionValueData = JSON.parse(dataEl.textContent);
          this.syncOptionValueDataToDom();
          this.updateAvailabilityFromOptionValues();
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          console.warn(error);
        });
    }

    /**
     * Sync option value data back onto option elements.
     */
    syncOptionValueDataToDom() {
      const optionsData = this.optionValueData && this.optionValueData.options;
      if (!optionsData) return;

      this.optionSelectors.forEach((selector) => {
        const optionData = optionsData.find((opt) => opt.name === selector.dataset.option);
        if (!optionData) return;

        const options = selector.querySelectorAll('.js-option:not([data-value=""])');
        options.forEach((optionEl) => {
          const optionValue = selector.dataset.selectorType === 'dropdown' ? optionEl.dataset.value : optionEl.value;
          const valueData = optionData.values.find((val) => val.value === optionValue);
          if (!valueData) return;

          optionEl.dataset.optionValueId = valueData.id || '';
          optionEl.dataset.optionAvailable = valueData.available;
          optionEl.dataset.optionSelected = valueData.selected;
          optionEl.dataset.optionVariantId = valueData.variant_id || '';
          optionEl.dataset.optionMediaId = valueData.media_id || '';
        });
      });
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      if (!this.useGranularAvailability) {
        return this.data.variants.find(
          (v) => v.options.every((val, index) => val === this.selectedOptions[index])
        );
      }

      const variantId = this.getSelectedVariantIdFromOptionValues();
      if (!variantId) return null;

      const variant = this.data.variants && this.data.variants.find((v) => String(v.id) === variantId);
      if (variant) return variant;

      const mediaId = this.getSelectedVariantMediaIdFromOptionValues();
      return {
        id: Number(variantId),
        available: this.getSelectedVariantAvailabilityFromOptionValues(),
        title: this.selectedOptions.filter(Boolean).join(' / '),
        featured_media: mediaId ? { id: mediaId } : null
      };
    }

    /**
     * Apply search parameters.
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${value}"]`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${value}"]`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false })
            );
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);

  document.addEventListener('mTac:b2b:refresh-prices', () => {
    window.mTacB2bLog && window.mTacB2bLog('[B2B] refresh-prices event');
    if (!VariantPicker.isB2BUnlocked()) return;
    const pickers = document.querySelectorAll('variant-picker');
    if (pickers.length) {
      pickers.forEach((picker) => {
        let variantId = null;
        if (typeof picker.getSelectedVariantIdFromOptionValues === 'function') {
          variantId = picker.getSelectedVariantIdFromOptionValues();
        }
        if (!variantId && picker.variant && picker.variant.id) {
          variantId = picker.variant.id;
        }
        if (!variantId) {
          const input = picker.closest('.js-product')?.querySelector('input[name="id"]');
          if (input && input.value) variantId = input.value;
        }
        window.mTacB2bLog && window.mTacB2bLog('[B2B] refresh-prices picker', { variantId });
        VariantPicker.refreshBssB2bPrice(variantId, picker.closest('.js-product') || document);
      });
      return;
    }

    const root = document.querySelector('.js-product') || document;
    const input = root.querySelector('input[name="id"]');
    const variantId = input && input.value ? input.value : null;
    window.mTacB2bLog && window.mTacB2bLog('[B2B] refresh-prices fallback', { variantId });
    VariantPicker.refreshBssB2bPrice(variantId, root);
  });
}
